import { useState } from 'react';
import {
  Anchor,
  Badge,
  Group,
  Progress,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import type { BillingDetail } from 'src/secret-menu/billing/hooks/useBilling';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import SectionCard from '../overview/SectionCard';
import {
  describePricing,
  PRICING_CATEGORY_COLORS,
  PRICING_CATEGORY_LABELS,
  PRICING_CATEGORY_ORDER,
  PRICING_CATEGORY_RULES,
  type PricingCategory,
} from './pricing';
import { formatUnits, formatRevenue } from './ui';

type Props = {
  firms: readonly BillingDetail[];
  onEditFirm: (companyId: string) => void;
};

const formatDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const SHORT_LABELS: Record<PricingCategory, string> = {
  TRIAL: 'Free trial',
  DISCOUNTED: 'Discounted',
  FIXED: 'Fixed fee',
  STANDARD: 'Standard',
};

const billOf = (firm: BillingDetail) => firm.monthlyBillPence ?? 0;

function Families({ firm, onAdd }: { firm: BillingDetail; onAdd: () => void }) {
  const billed = firm.activeBillableUnitCount;
  const stated = firm.statedBillableUnits;
  if (stated === null) {
    return (
      <Group gap={6} justify="flex-end" wrap="nowrap">
        <Text size="textSm">{formatUnits(billed)}</Text>
        <Anchor
          component="button"
          size="xs"
          onClick={event => {
            event.stopPropagation();
            onAdd();
          }}
        >
          add stated
        </Anchor>
      </Group>
    );
  }
  const percent = stated > 0 ? (billed / stated) * 100 : 0;
  return (
    <Stack gap={4} align="flex-end">
      <Text size="textSm">
        {formatUnits(billed)}{' '}
        <Text span size="textXs" c="dimmed">
          of {formatUnits(stated)}
        </Text>
      </Text>
      <Progress value={Math.min(percent, 100)} size="xs" w={120} color="green" />
    </Stack>
  );
}

/**
 * Every adviser firm in one place: how it is priced, what it pays today and what it would pay at
 * full adoption. Selecting a row opens its billing form.
 */
export default function FirmsTable({ firms, onEditFirm }: Props) {
  const [filter, setFilter] = useState<'all' | PricingCategory>('all');
  const shown = (filter === 'all' ? firms : firms.filter(firm => firm.pricingCategory === filter))
    .slice()
    .sort((a, b) => billOf(b) - billOf(a) || a.companyName.localeCompare(b.companyName));
  const countOf = (category: PricingCategory) =>
    firms.filter(firm => firm.pricingCategory === category).length;
  const ended = firms.filter(firm => firm.discountEnded);
  const uplift = ended.reduce((total, firm) => total + firm.standardPriceUpliftPence, 0);

  const paying = shown.filter(firm => firm.pricingCategory !== 'TRIAL');
  const totalPays = paying.reduce((total, firm) => total + billOf(firm), 0);
  // As the headline full adoption figure counts them: a firm with no stated families is held at
  // today's bill.
  const totalFull = paying.reduce(
    (total, firm) => total + (firm.fullAdoptionMonthlyBillPence ?? billOf(firm)),
    0,
  );

  return (
    <SectionCard title="Firm revenue" subtitle="Click a firm to edit its billing">
      <Stack gap="sm">
        <SegmentedControl
          size="xs"
          fullWidth
          value={filter}
          onChange={value => setFilter(value as 'all' | PricingCategory)}
          data={[
            { value: 'all', label: `All (${firms.length})` },
            ...PRICING_CATEGORY_ORDER.map(category => ({
              value: category,
              label: (
                <Tooltip label={PRICING_CATEGORY_RULES[category]} multiline maw={260} withArrow>
                  <span>{`${SHORT_LABELS[category]} (${countOf(category)})`}</span>
                </Tooltip>
              ),
            })),
          ]}
        />

        {ended.length > 0 && (
          <Text size="textSm" c="red">
            {ended.length} {ended.length === 1 ? 'discount has' : 'discounts have'} ended ·{' '}
            {formatRevenue(uplift)} a month more at £5 per family
          </Text>
        )}

        <Table.ScrollContainer minWidth={640}>
          <Table
            highlightOnHover
            verticalSpacing="sm"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Firm</Table.Th>
                <Table.Th ta="right">Pays a month</Table.Th>
                <Table.Th ta="right">
                  <Tooltip
                    label="Families billed (holding an active client) out of the families the firm told us it has"
                    multiline
                    maw={260}
                    withArrow
                  >
                    <span>Families billed</span>
                  </Tooltip>
                </Table.Th>
                <Table.Th ta="right">
                  <Tooltip
                    label="Every stated family at £5 a month, never below today's bill"
                    multiline
                    maw={260}
                    withArrow
                  >
                    <span>At full adoption</span>
                  </Tooltip>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {shown.map(firm => {
                const bill = billOf(firm);
                const full = firm.fullAdoptionMonthlyBillPence;
                const trial = firm.pricingCategory === 'TRIAL';
                let pays = (
                  <Text size="textSm" fw={FONT_WEIGHTS.semibold}>
                    {formatRevenue(bill)}
                  </Text>
                );
                if (trial) {
                  pays = (
                    <Text size="textSm" c="dimmed">
                      {bill > 0 ? `${formatRevenue(bill)} once started` : 'Not billing'}
                    </Text>
                  );
                }
                return (
                  <Table.Tr
                    key={firm.companyId}
                    onClick={() => onEditFirm(firm.companyId)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Table.Td>
                      <Group gap={8} wrap="nowrap">
                        <Text size="textSm" fw={FONT_WEIGHTS.semibold}>
                          {firm.companyName}
                        </Text>
                        <Badge
                          size="xs"
                          variant="light"
                          color={PRICING_CATEGORY_COLORS[firm.pricingCategory]}
                        >
                          {PRICING_CATEGORY_LABELS[firm.pricingCategory]}
                        </Badge>
                      </Group>
                      <Text size="textXs" c="dimmed">
                        {describePricing(firm)}
                        {firm.discountEndDate && (
                          <Text span inherit c={firm.discountEnded ? 'red' : 'dimmed'}>
                            {` · discount ${firm.discountEnded ? 'ended' : 'ends'} ${formatDate(firm.discountEndDate)}`}
                          </Text>
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">{pays}</Table.Td>
                    <Table.Td ta="right">
                      <Families firm={firm} onAdd={() => onEditFirm(firm.companyId)} />
                    </Table.Td>
                    <Table.Td ta="right">
                      {full === null ? (
                        <Text size="textSm" c="dimmed">
                          —
                        </Text>
                      ) : (
                        <>
                          <Text size="textSm">{formatRevenue(full)}</Text>
                          {!trial && full > bill && (
                            <Text size="textXs" c="green">
                              +{formatRevenue(full - bill)}
                            </Text>
                          )}
                        </>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {shown.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text size="textSm" c="dimmed">
                      No firms here.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {paying.length > 1 && (
                <Table.Tr
                  style={{
                    fontWeight: FONT_WEIGHTS.semibold,
                    background: 'var(--mantine-color-default-hover)',
                  }}
                >
                  <Table.Td>Total of paying firms</Table.Td>
                  <Table.Td ta="right">{formatRevenue(totalPays)}</Table.Td>
                  <Table.Td />
                  <Table.Td ta="right">{formatRevenue(totalFull)}</Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>
    </SectionCard>
  );
}
